import { zodResolver } from '@hookform/resolvers/zod';
import { InlineAlertType, Query } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { SourceName } from 'features/application/hooks/useApplicationTrackErrorEvents';
import { useStepStartedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import useUpdateApplication from 'features/application/hooks/useUpdateApplication';
import useCustomerApplicationOrigination from 'features/signup/hooks/useCustomerApplicationOrigination';
import { SSNVerificationSchema } from 'features/signup/SsnVerification/SsnVerification.schema';
import React, { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  KeyboardAvoidingView,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import GetSsnExistsQuery from 'services/graphql/queries/GetSsnExists';
import Button from 'shared/components/vxl/Button/Button';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isIOS } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useKeyboardVisibility from 'shared/hooks/keyboard/useKeyboardVisibility';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import theme from 'shared/styles/theme';
import { getKeyboardVerticalOffset } from 'shared/utils/keyboardUtils';
import { formatSSN, SSN_MAX_LENGTH } from 'shared/utils/string/ssn';
import { useQuery } from 'urql';

const SsnVerification = () => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();

  const { application, hasApplicationLoaded, isReapplication } =
    useCustomerApplicationOrigination({
      sourceName: SourceName.SSN_VERIFICATION,
    });
  const {
    updateApplication,
    isUpdateApplicationLoading,
    isUpdateApplicationSuccess,
    updateApplicationError,
  } = useUpdateApplication({
    applicationId: application?.id,
    sourceName: SourceName.SSN_VERIFICATION,
  });

  const [{ data }] = useQuery<Query>({
    query: GetSsnExistsQuery,
    requestPolicy: 'cache-and-network',
  });

  useStepStartedFocusAnalytics(
    hasApplicationLoaded
      ? {
          analyticsId: 'stepStarted',
          customAttributes: {
            step_name: 'ssn_verification',
            ...(isReapplication && {
              reapplication_flow: 'true',
            }),
          },
        }
      : undefined,
  );
  useStepStartedFocusAnalytics(
    isUpdateApplicationSuccess
      ? {
          analyticsId: 'ssnVerificationCompleted',
          customAttributes: {
            step_name: 'ssn_verification',
            ...(isReapplication && {
              reapplication_flow: 'true',
            }),
          },
        }
      : undefined,
  );

  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'ssnVerification',
  });
  const [isSsnVisible, setIsSsnVisible] = useState(false);
  const handleSsnMaskClicked = usePressableAnalytics({
    analyticsId: 'ssnMaskClicked',
  });

  const {
    control,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      ssnText: '',
    },
    resolver: zodResolver(SSNVerificationSchema),
  });

  const isKeyboardVisible = useKeyboardVisibility();
  const { height } = useWindowDimensions();

  const handleBackButtonAction = useCallback(() => {
    router.replace('/signup/citizenship');
  }, [router]);

  useBackHandler(handleBackButtonAction);

  const onSubmitEditingAction = async () => {
    if (isValid) {
      await onSubmit();
    }
  };

  const onSubmit = async () => {
    const formData = getValues();
    const ssn = formData.ssnText.replaceAll('-', '');
    await updateApplication({
      updates: {
        ssn: ssn,
      },
      nextRoute: '/signup/legal',
    });
  };

  return (
    <View style={styles.container} testID="inline-loader">
      <KeyboardAvoidingView
        style={styles.keyboardContainer}
        behavior={isIOS ? 'padding' : 'height'}
        keyboardVerticalOffset={getKeyboardVerticalOffset(
          height,
          isKeyboardVisible,
        )}
      >
        <ScrollView contentContainerStyle={styles.keyboardContainer}>
          <Stack.Screen
            options={{
              headerBackVisible: true,
              header: (props: NativeStackHeaderProps) => (
                <ExpoNavigationBar
                  {...props}
                  primaryOnPress={handleBackButtonAction}
                />
              ),
            }}
          />

          <View style={styles.personalInfoFormContainer}>
            {updateApplicationError ? (
              <View style={styles.inLineAlertContainer}>
                <InlineAlert
                  alertType={InlineAlertType.Error}
                  bodyText={translatedText('invalidSSN')}
                  iconKeyType="ERROR"
                />
              </View>
            ) : null}
            <TitleLgText numberOfLines={0} style={styles.heading}>
              {translatedText('title')}
            </TitleLgText>

            <BodyBaseText numberOfLines={0}>
              {translatedText('description')}
            </BodyBaseText>

            <Controller
              control={control}
              name="ssnText"
              render={({ field: { onChange, onBlur, value } }) => {
                const handleChange = (text: string) => {
                  onChange(formatSSN(text));
                  onBlur();
                };
                return (
                  <View style={styles.input}>
                    <VXLTextInput
                      testID="ssnField"
                      analyticsId="ssnFieldClicked"
                      labelText={translatedText('textField.ssn')}
                      errorText={errors.ssnText?.message}
                      onChangeText={handleChange}
                      onBlur={onBlur}
                      value={value}
                      autoFocus
                      placeholder="___-__-____"
                      maxLength={SSN_MAX_LENGTH}
                      secureTextEntry={!isSsnVisible}
                      inputMode="numeric"
                      autoComplete="off"
                      onSubmitEditing={onSubmitEditingAction}
                      rightIcon={{
                        iconKey: !isSsnVisible ? 'VIEW' : 'HIDE',
                        iconAccessibilityLabel: isSsnVisible
                          ? 'eye-close'
                          : 'eye-open',
                        onPress: () => {
                          handleSsnMaskClicked();
                          setIsSsnVisible((currentValue) => !currentValue);
                        },
                      }}
                    />
                  </View>
                );
              }}
            />
            <View style={styles.inLineAlertContainer}>
              <InlineAlert
                alertType={InlineAlertType.Info}
                bodyText={translatedText('infoMessage')}
                iconKeyType="LOCK"
              />
            </View>
          </View>
        </ScrollView>
        <View
          style={[
            styles.buttonContainer,
            {
              paddingBottom: isKeyboardVisible
                ? theme.spacing.xl
                : bottom + theme.spacing.m,
            },
          ]}
        >
          <Button
            label={translatedText('buttons.next')}
            isDisabled={!isValid || data?.user?.customerInfo?.ssnExists}
            isLoading={isUpdateApplicationLoading}
            onPress={onSubmit}
            size="LARGE"
            type="PRIMARY"
            width="FULL"
          />
        </View>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    marginHorizontal: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingVertical: theme.spacing.m,
  },
  heading: {
    marginBottom: theme.spacing.m,
  },
  inLineAlertContainer: {
    paddingVertical: theme.spacing.m,
  },
  input: {
    marginTop: theme.spacing.m,
    width: '100%',
  },
  keyboardContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  personalInfoFormContainer: {
    flex: 1,
    paddingHorizontal: theme.spacing.m,
  },
});

export default SsnVerification;
